import styled from "styled-components";
import { Heading5 } from "--components/typography";
import { cssBreakpoints } from "--components/hooks/useBreakpoint";

type FormProps = {
    padding?: number | string
    spacing?: number | string
}


export namespace Form {

    /**
     * A basic wrapper for settings forms
     */
    export const SettingsForm = styled.form`
        display: flex;
        flex-direction: column;
        max-width: 820px;
        margin: ${props => props.theme.spacing(4, 2)};

        ${cssBreakpoints.mobile} {
            margin: ${props => props.theme.spacing(2, 0)};
        }
    `;

    export const AuthForm = styled.form`
        display: flex;
        flex-direction: column;
        gap: ${props => props.theme.spacing(4)};
        padding: ${props => props.theme.spacing(2)};

        ${cssBreakpoints.mobile} {
            gap: ${props => props.theme.spacing(2)};
            padding: ${props => props.theme.spacing(1)};
        }      

        &.slide-enter{
            opacity: 0;
            transform: translateX(-50%);
        }
        &.slide-exit{
            opacity: 1;
            transform: translateX(0);
        }
        &.slide-enter-active{
            opacity: 1;
            transform: translateX(0);
            transition: all ${props => props.theme.transitions.standard} ease-out;

        }
        &.slide-exit-active{
            opacity: 0;
            transform: translateX(50%);
            transition: all ${props => props.theme.transitions.standard} ease-in;
        }
    `;

    export const BasicForm = styled.div<FormProps>`
        display: flex;
        flex-direction: column;
        gap: ${props => typeof props.spacing == "number"
            ? props.theme.spacing(props.spacing)
            : props.spacing || props.theme.spacing(2)
        };
        padding: ${props =>
            typeof props.padding == "number" ?
                props.theme.spacing(props.padding) :
                props.padding || 0
        };
    `;

    /**  
     * A responsive split layout setting an avatar/icon and rendering form fields 
     * to the right or below
    */
    export const AvatarSplitLayout = styled.div`
        display: flex;
        flex-direction: row;
        gap: ${props => props.theme.spacing(4)};
        flex-wrap: wrap;
        justify-content: center;

        >*:nth-child(1) {
            flex: 0 0 auto;
            display: flex;
            flex-direction: column;
            gap: ${props => props.theme.spacing(1)};
            align-items: center;
            padding: ${props => props.theme.spacing(2, 0, 0, 0)};
        }
        >*:nth-child(2) {
            flex: 1 0 275px;
        }
    `;

    /**
     * A way of sectioning form put inputs relating to each other in the same section
     */
    export const FormSection = styled.div`
        margin: ${props => props.theme.spacing(0, 0, 2, 0)};
    `;

    /**
     * Should be used to apply a title to all but the first FormSection
     */
    export const FormSubheading = styled(Heading5)`
        display: flex;
        margin: ${props => props.theme.spacing(0, 0, 1, 0)};
        flex-wrap: nowrap;
        align-items: center;
        >:first-child {
            flex: 1 0 auto;
        }
    `;

    /**
     * Applies consistent spacing between form fields
     */
    export const FormFields = styled.div`
        display: flex;
        flex-direction: column;
        gap: ${props => props.theme.spacing(2)};
    `;

    /**
     * Use HorizontalSections with HorizontalSection to group multiple form 
     * sections on the same row in a responsive manner.
     */
    export const HorizontalSections = styled.div`
        display: flex;
        gap: 0 ${props => props.theme.spacing(2)};
        flex-wrap: wrap;
    `;

    /** 
     * Use inside HorizontalSections to create equally sized containers for 
     * form sections.
    */
    export const HorizontalSection = styled.div`
        flex: 1;
    `;

    /**
     * Place form actions in here
     * Todo: This should probably dock 
     */
    export const ActionsGutter = styled.div<{ mobile?: boolean }>`
        margin-top: auto;
        display: flex;
        gap: ${props => props.theme.spacing(props.mobile ? 1 : 2)};
        align-items: center;
        justify-content: end;
    `;

    /** Centered version of ActionsGutter, place form actions in here */
    export const CenteredActionGutter = styled(ActionsGutter)`
        justify-content: center;
    `;

    /** All elements will be moved to the right side except the first one creating the gap between them */
    export const RightSided = styled.div`
        display: flex;
        align-items: center;
        margin: ${props => props.theme.spacing(0, 0, 1, 0)};
        
        >:first-child {
            margin-right: auto;
        }
    `
}

export default Form;
